import { useCallback, useId, useState, type ReactNode } from "react";
import { OPPORTUNITY_OPEN_STAGE_VALUES, OPPORTUNITY_STAGE_VALUES, type OpportunityStageCounts, type OpportunityStageValue, type OpportunityView } from "@gioia365/contracts";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTab } from "../api/queries";
import { Button } from "../design/components/Button";
import { DataTable } from "../design/components/DataTable";
import { Drawer } from "../design/components/Drawer";
import { Icon, type IconName } from "../design/components/Icon";
import { Reason } from "../design/components/Reason";
import { Segmented } from "../design/components/Segmented";
import { Select } from "../design/components/Select";
import { StatusPill } from "../design/components/StatusPill";
import { messageForError } from "../i18n/errors";
import { formatCount, formatEuroCents, formatNumericDate, formatNumericDateTime } from "../i18n/format";
import { useLocale, useT, type Translate } from "../i18n/locale";
import { SEARCH_MAX_LENGTH, useResultSearch } from "../leads/lead-list-hooks";
import { leadOwnerOptionsQuery, useLeadView } from "../leads/lead-queries";
import { opportunityScopeOf, taskScopeOf, useAccess } from "../session/access";
import { OPPORTUNITIES_SOURCE } from "../tasks/task-search";
import { NewOpportunityDialog } from "./NewOpportunityDialog";
import { OpportunitySteps } from "./OpportunityDialog";
import { opportunitiesPagesQuery, opportunitiesSummaryQuery, opportunityDetailQuery, ownOpportunitiesOf, type OpportunitiesRequest } from "./opportunity-queries";
import { opportunitiesFiltersOf, validateOpportunitiesSearch, WHICH_PARAMS, type OpportunitiesSearch, type WhichParam } from "./opportunity-search";
import { currentMonthOf, leadNameOf, lostReasonText, nextStepText, productText, STAGE_TONES, stageLabel } from "./opportunity-texts";
import "../design/components/page-header.css";
import "../leads/lead-list.css";
import "./opportunities.css";

// «Le opportunità» (piano M6, R8, Task 25; mock-up `opportunita.html` e `initOpportunities` di `mockup.js`; SEZ-1, SEZ-9, SEZ-10):
// le Opportunità nel perimetro dell'area `opportunities` di chi guarda. In cima il conto delle fasi aperte («Disco da fare», «Disco fatte,
// demo da prenotare», «Demo da fare», «Demo fatte, da chiudere»), con i filtri e qualunque sia «Quali», e quello del mese di adesso nel
// fuso dell'azienda («Vendute a ottobre» con il valore, «Perse a ottobre»), come il mock-up. Due modi di vederle: «Per fase», una
// colonna per fase con il suo conto e le sue carte, ognuna letta a pagine per conto suo con il suo «Mostra altre» (così una fase con
// molte Opportunità non lascia vuote le colonne dopo di lei), senza trascinare (la fase si cambia soltanto nella scheda del Lead,
// SEZ-10); «Tabella», una riga per Opportunità in ordine di fase e poi di data, a pagine. «Quali» (le aperte di partenza, le vendute, le
// perse, tutte), «Di chi», la campagna e la ricerca stanno nell'indirizzo (`opportunity-search.ts`). «Di chi» è di chi le vede tutte e
// non è un Commerciale: il Commerciale (livello 1; nella «Guarda come…» la persona osservata) vede soltanto le sue, e ogni lettura,
// il conto in cima compreso, le chiede per lui (SEZ-1, `ownOpportunitiesOf`); gli altri partono da «Tutto il team». Una carta o il nome
// nella tabella aprono il dettaglio «Opportunità di …», con le fasi raggiunte, i dati, «Com'è andata» e «Aggiorna nella scheda del
// Lead», che apre la scheda con la sua finestra «Opportunità» (`?opportunita=1&da=opportunita`, come il mock-up). Sotto i 720 px di
// contenuto le colonne stanno una sotto l'altra e la tabella scorre nel suo riquadro, mai la pagina.

const WHICH: readonly WhichParam[] = ["aperte", "vendute", "perse", "tutte"];

/** Le colonne di «Per fase» per «Quali»: le quattro aperte, una delle chiuse o tutte e sei. */
const COLUMNS: Readonly<Record<WhichParam, readonly OpportunityStageValue[]>> = {
  aperte: OPPORTUNITY_OPEN_STAGE_VALUES,
  vendute: ["sold"],
  perse: ["lost"],
  tutte: OPPORTUNITY_STAGE_VALUES,
};

/** Quante sono le Opportunità di «Quali», dal conto per fase. */
function whichCount(counts: OpportunityStageCounts, which: WhichParam): number {
  if (which === "vendute") return counts.sold;
  if (which === "perse") return counts.lost;
  const stages = which === "aperte" ? OPPORTUNITY_OPEN_STAGE_VALUES : OPPORTUNITY_STAGE_VALUES;
  return stages.reduce((sum, stage) => sum + counts[stage], 0);
}

/** Lo stesso indirizzo senza i valori tolti: un filtro tornato a «Tutte» non resta scritto. */
function compact(search: OpportunitiesSearch): OpportunitiesSearch {
  return Object.fromEntries(Object.entries(search).filter(([, value]) => value !== undefined)) as OpportunitiesSearch;
}

/** Il conto del mese di adesso: il suo nome, le vendute con il loro valore e le perse; `null` finché non arriva. */
type MonthTotals = { name: string; sold: number; soldValueCents: number; lost: number } | null;

export function OpportunitiesPage() {
  const t = useT();
  const { locale } = useLocale();
  const tab = useTab();
  const access = useAccess();
  const view = useLeadView();
  const baseId = useId();
  // Di nuovo dalla forma, come «Conversazioni»: un valore scartato resta grezzo nella ricerca della rotta e non deve arrivare al server.
  const search = validateOpportunitiesSearch(useSearch({ from: "/autenticato/guscio/azienda/opportunita" }));
  const navigate = useNavigate({ from: "/opportunita" });
  const scope = opportunityScopeOf(access, "view");
  const canView = scope !== "none";
  // Il Commerciale soltanto le sue (SEZ-1); «Di chi» per chi le vede tutte e non è un Commerciale, con la regola del server.
  const own = ownOpportunitiesOf(access);
  const byPerson = scope === "all" && own === null;
  const filters: OpportunitiesRequest = { ...opportunitiesFiltersOf(search, byPerson), ...(own === null ? {} : { who: own }) };
  const which = search.quali ?? "aperte";
  const layout = search.vista ?? "fasi";
  const timeZone = access.context.settings.timeZone;
  // Il conto per fase con i filtri (una riga sola), e lo stesso conto nel mese di adesso: il periodo guarda la chiusura delle vendute e
  // delle perse. «Adesso» è l'apertura della pagina o, dopo, l'ultima lettura del riepilogo: a pagina aperta il mese cambia con lei.
  const summary = useQuery({ ...opportunitiesSummaryQuery(tab, filters, view), enabled: canView, placeholderData: keepPreviousData });
  const [openedAt] = useState(() => Date.now());
  const month = currentMonthOf(locale, timeZone, Math.max(openedAt, summary.dataUpdatedAt));
  const monthSummary = useQuery({
    ...opportunitiesSummaryQuery(tab, { ...filters, period: "custom", from: month.from, to: month.to }, view),
    enabled: canView,
    placeholderData: keepPreviousData,
  });
  const people = useQuery({ ...leadOwnerOptionsQuery(tab), enabled: canView && byPerson });
  const counts = summary.data?.counts;
  const monthTotals: MonthTotals = monthSummary.data
    ? { name: month.name, sold: monthSummary.data.counts.sold, soldValueCents: monthSummary.data.soldValueCents, lost: monthSummary.data.counts.lost }
    : null;
  const [opened, setOpened] = useState<OpportunityView | null>(null);
  const [creating, setCreating] = useState(false);
  // «Nuova opportunità» prenota una disco o una demo: la regola di «Prenota disco o demo» della scheda (`canSetCallback`), senza la
  // scheda, più la funzione «call» che il server chiede per prenotare; la modifica del Lead la controlla il server.
  const canCreate = canView && access.can("call") && (access.context.commercialView?.membershipId ?? null) === view && taskScopeOf(access, "create") === "all";

  const update = useCallback(
    (change: Partial<OpportunitiesSearch>) => void navigate({ search: (previous) => compact({ ...validateOpportunitiesSearch(previous), ...change }), replace: true }),
    [navigate],
  );
  const writeQ = useCallback((next: string | undefined) => update({ q: next }), [update]);
  const { text, setText } = useResultSearch(search.q, writeQ);

  const layoutOptions = [
    { value: "fasi", label: t("opportunities.layouts.board") },
    { value: "tabella", label: t("opportunities.layouts.table") },
  ];
  const whichOptions = WHICH.map((id) => ({
    value: id,
    label: t(`opportunities.views.${id === "aperte" ? "open" : id === "vendute" ? "sold" : id === "perse" ? "lost" : "all"}`),
    ...(counts ? { count: formatCount(locale, whichCount(counts, id)) } : {}),
  }));
  // Le persone che possono avere Lead; una persona dell'indirizzo che non c'è (sospesa, un link di prima) resta scelta con il suo
  // identificativo.
  const personOptions = [{ value: "all", label: t("opportunities.wholeTeam") }, ...(people.data ?? []).map((person) => ({ value: person.membershipId, label: person.displayName }))];
  if (search.chi !== undefined && !personOptions.some((option) => option.value === search.chi)) personOptions.push({ value: search.chi, label: search.chi });
  const campaignOptions = [{ value: "", label: t("opportunities.allCampaigns") }, ...(summary.data?.campaigns ?? []).map((name) => ({ value: name, label: name }))];
  if (search.campagna !== undefined && !campaignOptions.some((option) => option.value === search.campagna)) campaignOptions.push({ value: search.campagna, label: search.campagna });
  const filtered = search.q !== undefined || search.campagna !== undefined || (byPerson && search.chi !== undefined);

  let results: ReactNode;
  if (!canView) {
    results = <Reason tone="warning">{t("errors.permission_denied")}</Reason>;
  } else if (!counts || summary.isPlaceholderData) {
    results = summary.isError ? (
      <OpportunitiesProblem error={summary.error} onRetry={() => void summary.refetch()} />
    ) : (
      <p className="g-opportunities__loading" role="status">
        {t("common.loading")}
      </p>
    );
  } else if (whichCount(counts, which) === 0) {
    results = (
      <div className="g-opportunities__empty">
        <p>
          <strong>{t("opportunities.empty")}</strong>
        </p>
        <p>{filtered ? t("opportunities.emptyFiltered") : t("opportunities.emptyHint")}</p>
      </div>
    );
  } else {
    results = (
      <>
        {summary.isError ? <OpportunitiesProblem error={summary.error} onRetry={() => void summary.refetch()} /> : null}
        {layout === "fasi" ? (
          <div className="g-opportunities__board">
            {COLUMNS[which].map((stage) => (
              <BoardColumn
                key={stage}
                filters={filters}
                stage={stage}
                count={counts[stage]}
                soldValueCents={summary.data?.soldValueCents ?? 0}
                timeZone={timeZone}
                onOpen={setOpened}
              />
            ))}
          </div>
        ) : (
          <OpportunitiesTable filters={filters} stage={WHICH_PARAMS[which]} timeZone={timeZone} onOpen={setOpened} />
        )}
      </>
    );
  }

  return (
    <div className="g-opportunities">
      <div className="g-page-head g-opportunities__head">
        <div>
          <p className="g-page-head__eyebrow">{t("shell.nav.opportunities")}</p>
          <div className="g-opportunities__title">
            <h1 className="g-page-head__title">{t("opportunities.title")}</h1>
            {counts ? (
              <span className="g-opportunities__count" role="status">
                {t("opportunities.openCount", { count: formatCount(locale, whichCount(counts, "aperte")) })}
              </span>
            ) : null}
          </div>
          <p className="g-opportunities__intro">{t("opportunities.intro")}</p>
        </div>
        <div className="g-opportunities__head-actions">
          {canCreate ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Icon name="plus" />
              {t("opportunities.newButton")}
            </Button>
          ) : null}
          {canView ? <Segmented label={t("opportunities.layoutLabel")} options={layoutOptions} value={layout} onChange={(value) => update({ vista: value === "tabella" ? "tabella" : undefined })} /> : null}
        </div>
      </div>
      {creating ? <NewOpportunityDialog tab={tab} timeZone={timeZone} onClose={() => setCreating(false)} /> : null}
      {canView && counts ? <Totals counts={counts} month={monthTotals} monthName={month.name} /> : null}
      {canView ? (
        <div className="g-opportunities__toolbar">
          <Segmented
            label={t("opportunities.viewsLabel")}
            options={whichOptions}
            value={which}
            onChange={(value) => update({ quali: value === "vendute" || value === "perse" || value === "tutte" ? value : undefined })}
          />
          <div className="g-opportunities__filters">
            {byPerson ? (
              <Select id={`${baseId}-who`} label={t("opportunities.who")} value={search.chi ?? "all"} options={personOptions} onChange={(value) => update({ chi: value === "all" ? undefined : value })} />
            ) : null}
            <Select
              id={`${baseId}-campaign`}
              label={t("opportunities.campaign")}
              value={search.campagna ?? ""}
              options={campaignOptions}
              onChange={(value) => update({ campagna: value === "" ? undefined : value })}
            />
          </div>
          <div className="g-lead-search g-opportunities__search">
            <Icon name="search" className="g-lead-search__icon" />
            <label className="g-visually-hidden" htmlFor={`${baseId}-search`}>
              {t("opportunities.search")}
            </label>
            <input
              id={`${baseId}-search`}
              className="g-field__input"
              type="search"
              value={text}
              maxLength={SEARCH_MAX_LENGTH}
              autoComplete="off"
              placeholder={t("opportunities.search")}
              onChange={(event) => setText(event.target.value)}
            />
          </div>
        </div>
      ) : null}
      <div className="g-opportunities__results" aria-busy={canView && !counts && !summary.isError ? true : undefined}>
        {results}
      </div>
      {opened ? <OpportunityDetail opportunity={opened} timeZone={timeZone} onClose={() => setOpened(null)} /> : null}
    </div>
  );
}

function OpportunitiesProblem({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const t = useT();
  return (
    <div className="g-opportunities__problem">
      <Reason tone="warning" role="alert">
        {messageForError(t, error)}
      </Reason>
      <Button onClick={onRetry}>{t("common.retry")}</Button>
    </div>
  );
}

/**
 * «In cima, a che punto è il lavoro» (mock-up): le quattro fasi aperte con i filtri, poi le vendute del mese con il loro valore e le
 * perse del mese, nel fuso dell'azienda («Vendute a ottobre»). Il mese arriva con la sua lettura: prima, «—».
 */
function Totals({ counts, month, monthName }: { counts: OpportunityStageCounts; month: MonthTotals; monthName: string }) {
  const t = useT();
  const { locale } = useLocale();
  const value = (stage: OpportunityStageValue): string => {
    if (stage === "sold") return month ? t("opportunities.countWithValue", { count: formatCount(locale, month.sold), value: formatEuroCents(locale, month.soldValueCents) }) : "—";
    if (stage === "lost") return month ? formatCount(locale, month.lost) : "—";
    return formatCount(locale, counts[stage]);
  };
  return (
    <div className="g-opportunities__totals" role="group" aria-label={t("opportunities.totalsLabel")}>
      <dl>
        {OPPORTUNITY_STAGE_VALUES.map((stage) => (
          <div key={stage} data-tone={STAGE_TONES[stage]}>
            <dt>{t(`opportunities.totals.${stage}`, { month: monthName })}</dt>
            <dd>{value(stage)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

type ColumnProps = {
  filters: OpportunitiesRequest;
  stage: OpportunityStageValue;
  /** Il conto della fase dal riepilogo, finché la colonna non ha il suo. */
  count: number;
  /** Il valore delle vendute con i filtri, accanto al conto di «Venduto». */
  soldValueCents: number;
  timeZone: string;
  onOpen: (opportunity: OpportunityView) => void;
};

/**
 * Una colonna di «Per fase»: le Opportunità della sua fase, lette a pagine per conto suo, con il conto di tutta la fase e «Mostra
 * altre» finché ce ne sono. «Nessuna» soltanto quando la fase non ne ha davvero.
 */
function BoardColumn({ filters, stage, count, soldValueCents, timeZone, onOpen }: ColumnProps) {
  const t = useT();
  const { locale } = useLocale();
  const tab = useTab();
  const view = useLeadView();
  const pages = useInfiniteQuery({ ...opportunitiesPagesQuery(tab, { ...filters, stage }, view), placeholderData: keepPreviousData });
  const rows = pages.data && !pages.isPlaceholderData ? pages.data.pages.flatMap((page) => page.rows) : null;
  const total = rows === null ? count : (pages.data?.pages[0]?.total ?? count);
  const shownCount = formatCount(locale, total);
  const label = stageLabel(t, stage);
  let body: ReactNode;
  if (rows === null) {
    body = pages.isError ? (
      <OpportunitiesProblem error={pages.error} onRetry={() => void pages.refetch()} />
    ) : (
      <p className="g-opportunities__loading" role="status">
        {t("common.loading")}
      </p>
    );
  } else if (rows.length === 0 && total === 0) {
    body = <p className="g-opportunities__none">{t("opportunities.none")}</p>;
  } else {
    body = (
      <>
        <ul>
          {rows.map((row) => (
            <li key={row.id}>
              <OpportunityCard opportunity={row} timeZone={timeZone} onOpen={onOpen} />
            </li>
          ))}
        </ul>
        {pages.isError ? <OpportunitiesProblem error={pages.error} onRetry={() => void pages.refetch()} /> : null}
        {pages.hasNextPage ? (
          <Button onClick={() => void pages.fetchNextPage()} disabled={pages.isFetchingNextPage}>
            {t("opportunities.more")}
          </Button>
        ) : null}
      </>
    );
  }
  return (
    <section className="g-opportunities__column" data-tone={STAGE_TONES[stage]} aria-label={label} aria-busy={rows === null && !pages.isError ? true : undefined}>
      <h2>
        <span>{label}</span>{" "}
        <span className="g-opportunities__column-count">{stage === "sold" ? t("opportunities.countWithValue", { count: shownCount, value: formatEuroCents(locale, soldValueCents) }) : shownCount}</span>
      </h2>
      {body}
    </section>
  );
}

/** L'icona del prossimo passo: la vendita, la perdita, da riprogrammare, un appuntamento. */
function nextIcon(opportunity: OpportunityView): IconName {
  if (opportunity.stage === "sold") return "check";
  if (opportunity.stage === "lost") return "close";
  return opportunity.nextStep === "reschedule" ? "alert" : "calendar";
}

/** Una carta di «Per fase»: un pulsante che apre il dettaglio, con il nome, il salone, il prossimo passo, chi la segue e la campagna. */
function OpportunityCard({ opportunity, timeZone, onOpen }: { opportunity: OpportunityView; timeZone: string; onOpen: (opportunity: OpportunityView) => void }) {
  const t = useT();
  const { locale } = useLocale();
  return (
    <button type="button" className="g-opportunities__card" aria-haspopup="dialog" onClick={() => onOpen(opportunity)}>
      <strong>{leadNameOf(opportunity)}</strong>
      {opportunity.lead.salonName ? <span className="g-opportunities__salon">{opportunity.lead.salonName}</span> : null}
      <span className="g-opportunities__next" data-reschedule={opportunity.nextStep === "reschedule" ? "true" : undefined}>
        <Icon name={nextIcon(opportunity)} />
        {nextStepText(t, locale, timeZone, opportunity)}
      </span>
      <span className="g-opportunities__foot">
        <span>{opportunity.assignee.displayName}</span>
        {opportunity.campaign ? <span>{opportunity.campaign}</span> : null}
      </span>
    </button>
  );
}

type TableProps = {
  filters: OpportunitiesRequest;
  /** La fase di «Quali»: `open` le quattro aperte, nessuna per tutte. */
  stage: OpportunitiesRequest["stage"];
  timeZone: string;
  onOpen: (opportunity: OpportunityView) => void;
};

/** «Tabella»: una riga per Opportunità di «Quali», in ordine di fase e poi di data, a pagine, con il nome che apre il dettaglio. */
function OpportunitiesTable({ filters, stage, timeZone, onOpen }: TableProps) {
  const t = useT();
  const { locale } = useLocale();
  const tab = useTab();
  const view = useLeadView();
  const pages = useInfiniteQuery({ ...opportunitiesPagesQuery(tab, { ...filters, ...(stage === undefined ? {} : { stage }) }, view), placeholderData: keepPreviousData });
  const rows = pages.data && !pages.isPlaceholderData ? pages.data.pages.flatMap((page) => page.rows) : null;
  if (rows === null) {
    return pages.isError ? (
      <OpportunitiesProblem error={pages.error} onRetry={() => void pages.refetch()} />
    ) : (
      <p className="g-opportunities__loading" role="status">
        {t("common.loading")}
      </p>
    );
  }
  // Un dato che manca è «—», come nel mock-up.
  const none = <span className="g-opportunities__muted">—</span>;
  return (
    <>
      {pages.isError ? <OpportunitiesProblem error={pages.error} onRetry={() => void pages.refetch()} /> : null}
      <div className="g-opportunities__table">
        <DataTable caption={t("opportunities.caption")}>
          <thead>
            <tr>
              <th scope="col">{t("opportunities.columns.lead")}</th>
              <th scope="col">{t("opportunities.columns.stage")}</th>
              <th scope="col">{t("opportunities.columns.next")}</th>
              <th scope="col">{t("opportunities.columns.assignee")}</th>
              <th scope="col">{t("opportunities.columns.bookedBy")}</th>
              <th scope="col">{t("opportunities.columns.closedBy")}</th>
              <th scope="col">{t("opportunities.columns.value")}</th>
              <th scope="col">{t("opportunities.columns.campaign")}</th>
              <th scope="col">{t("opportunities.columns.openedAt")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <th scope="row">
                  <button type="button" className="g-opportunities__name" aria-haspopup="dialog" onClick={() => onOpen(row)}>
                    {leadNameOf(row)}
                  </button>
                  {row.lead.salonName ? <span className="g-table__sub">{row.lead.salonName}</span> : null}
                </th>
                <td>
                  <StatusPill tone={STAGE_TONES[row.stage]} icon={row.stage === "sold" ? "check" : row.stage === "lost" ? "minus" : "clock"}>
                    {stageLabel(t, row.stage)}
                  </StatusPill>
                </td>
                <td>{nextStepText(t, locale, timeZone, row)}</td>
                <td>{row.assignee.displayName}</td>
                <td>{row.bookedBy?.displayName ?? none}</td>
                <td>{row.closedBy?.displayName ?? none}</td>
                <td>{row.sale ? formatEuroCents(locale, row.sale.valueCents) : none}</td>
                <td>{row.campaign ?? none}</td>
                <td>{formatNumericDateTime(timeZone, row.openedAt)}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      </div>
      {pages.hasNextPage ? (
        <div>
          <Button onClick={() => void pages.fetchNextPage()} disabled={pages.isFetchingNextPage}>
            {t("opportunities.more")}
          </Button>
        </div>
      ) : null}
    </>
  );
}

type HistoryLine = { at: string; who: string | null; what: string };

/**
 * «Com'è andata»: l'apertura con chi ha prenotato la disco, gli appuntamenti dal più vecchio (in programma, fatti, il Lead non si è
 * presentato, annullati) e la chiusura con chi ha chiuso la vendita. Ogni passo è anche un evento della Timeline del Lead.
 */
function historyOf(t: Translate, locale: ReturnType<typeof useLocale>["locale"], opportunity: OpportunityView, appointments: readonly NonNullable<OpportunityView["appointment"]>[]): HistoryLine[] {
  const lines: HistoryLine[] = [{ at: opportunity.openedAt, who: opportunity.bookedBy?.displayName ?? null, what: t("opportunities.detail.opened") }];
  const ordered = [...appointments].sort((left, right) => Date.parse(left.startsAt) - Date.parse(right.startsAt));
  for (const appointment of ordered) {
    const kind = t(`appointments.kind.${appointment.kind}`);
    const state = appointment.status === "done" ? (appointment.result === "no_show" ? "no_show" : "held") : appointment.status;
    lines.push({ at: appointment.startsAt, who: null, what: t(`opportunities.detail.appointment.${state}`, { kind }) });
  }
  if (opportunity.closedAt && opportunity.stage === "sold" && opportunity.sale) {
    const what = t("opportunities.detail.sold", { product: productText(t, opportunity.sale.product), value: formatEuroCents(locale, opportunity.sale.valueCents) });
    lines.push({ at: opportunity.closedAt, who: opportunity.closedBy?.displayName ?? null, what });
  }
  if (opportunity.closedAt && opportunity.stage === "lost" && opportunity.lost) {
    lines.push({ at: opportunity.closedAt, who: null, what: t("opportunities.detail.lost", { reason: lostReasonText(t, opportunity.lost) }) });
  }
  return lines;
}

/** Il dettaglio «Opportunità di …» (mock-up `m-opp-detail`): si legge qui, la fase si cambia nella scheda del Lead. */
function OpportunityDetail({ opportunity, timeZone, onClose }: { opportunity: OpportunityView; timeZone: string; onClose: () => void }) {
  const t = useT();
  const { locale } = useLocale();
  const tab = useTab();
  const view = useLeadView();
  const detail = useQuery(opportunityDetailQuery(tab, opportunity.id, view));
  const current = detail.data?.opportunity ?? opportunity;
  const appointments = detail.data?.appointments ?? [];
  const name = leadNameOf(current);
  const status = current.lead.status.name;
  const facts: Array<[string, string]> = [
    [t("opportunities.detail.stage"), stageLabel(t, current.stage)],
    [t("opportunities.detail.next"), nextStepText(t, locale, timeZone, current)],
    [t("opportunities.detail.owner"), current.lead.owner?.displayName ?? t("leadCard.noOwner")],
    [t("opportunities.detail.bookedBy"), current.bookedBy?.displayName ?? "—"],
  ];
  if (current.sale) {
    facts.push(
      [t("opportunities.detail.closedBy"), current.closedBy?.displayName ?? "—"],
      [t("opportunities.detail.product"), productText(t, current.sale.product)],
      [t("opportunities.detail.value"), formatEuroCents(locale, current.sale.valueCents)],
      [t("opportunities.detail.contract"), current.sale.contractType ?? "—"],
      [t("opportunities.detail.offer"), current.sale.offer ?? "—"],
      [t("opportunities.detail.payFrom"), current.sale.paymentStartDate ? formatNumericDate(timeZone, current.sale.paymentStartDate) : "—"],
      [t("opportunities.detail.bonus"), current.sale.bonus ?? "—"],
    );
  }
  facts.push([t("opportunities.detail.campaign"), current.campaign ?? "—"], [t("opportunities.detail.openedAt"), formatNumericDateTime(timeZone, current.openedAt)]);

  return (
    <Drawer
      open
      title={t("opportunities.detail.title", { name })}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("common.close")}</Button>
          <Link to="/lead/$leadId" params={{ leadId: current.lead.id }} search={{ opportunita: 1, da: OPPORTUNITIES_SOURCE }} className="g-button" data-variant="primary">
            {t("opportunities.detail.open")}
          </Link>
        </>
      }
    >
      <div className="g-opportunities__detail">
        <p className="g-opportunities__muted">
          {current.lead.salonName ? t("opportunities.detail.status", { salon: current.lead.salonName, status }) : t("opportunities.detail.statusNoSalon", { status })}
        </p>
        <OpportunitySteps opportunity={current} demoSeen={appointments.some((appointment) => appointment.kind === "demo")} />
        <dl className="g-opportunities__facts">
          {facts.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
        <h3 className="g-opportunities__history-title">{t("opportunities.detail.history")}</h3>
        {detail.isError ? (
          <OpportunitiesProblem error={detail.error} onRetry={() => void detail.refetch()} />
        ) : detail.data ? (
          <ol className="g-opportunities__history">
            {historyOf(t, locale, current, appointments).map((line, index) => (
              <li key={`${line.at}-${index}`}>
                <strong>{formatNumericDateTime(timeZone, line.at)}</strong>
                {line.who ? <> · {line.who}</> : null}
                <br />
                {line.what}
              </li>
            ))}
          </ol>
        ) : (
          <p className="g-opportunities__loading" role="status">
            {t("common.loading")}
          </p>
        )}
        <p className="g-opportunities__muted">{t("opportunities.detail.howTo")}</p>
      </div>
    </Drawer>
  );
}
